// Pure gait shaping curves. NO imports on purpose: harness/curves.mjs measures these functions
// offline, the same way harness/audio.mjs measures the DSP. A curve you can only judge by looking
// at the walk is a curve nobody is checking.

/** Cubic ease with ZERO derivative at both ends -- that property is the whole point here. */
export function easeInOutCubic(u) {
  const t = u < 0 ? 0 : u > 1 ? 1 : u;
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Pelvic weight-bearing dip, 0 (tall) .. 1 (deepest), twice per gait cycle.
 *
 * The old curve was |sin(2*pi*t)|, which has a CUSP at every zero crossing: the body arrives at
 * the top of its arc with velocity +v and leaves with -v, an instantaneous reversal. That is the
 * "jarring at the stance transition" -- it is a real discontinuity in the first derivative, not a
 * matter of taste. Feeding a triangle through easeInOutCubic flattens BOTH extremes (the ease has
 * zero slope at 0 and 1), so the pelvis eases into and out of every dip. Fenced in
 * harness/curves.mjs by the largest first-derivative jump.
 */
export function gaitDip(t) {
  const u = (t * 2) % 1;
  return easeInOutCubic(1 - Math.abs(2 * u - 1));
}

/**
 * Heel-to-toe foot roll, in DEGREES, using the brief's own sign convention: + is toes UP.
 * (The rig's foot.rotation.x is + for toes DOWN, so the caller negates. Keeping the pure function
 * in the brief's convention means the fence prints the brief's numbers.)
 *
 *   heel strike (phase 0)     +10   toes up, heel lands first
 *   mid-stance  (45% of stance) 0   flat, weight passes over the foot
 *   toe-off     (end of stance) -15 toes down, the foot pushes off
 *   swing                       recovers -15 -> +10, dorsiflexing to clear the ground
 *
 * Every segment is an easeInOutCubic, so the curve is continuous AND has matching (zero) slope at
 * each junction -- including the wrap from phase 1 back to phase 0.
 */
export function footRoll(phase, duty) {
  const HEEL = 10, TOE = -15, MID = 0.45;
  const ph = ((phase % 1) + 1) % 1;
  if (ph < duty) {
    const u = ph / duty;
    if (u < MID) return HEEL * (1 - easeInOutCubic(u / MID));
    return TOE * easeInOutCubic((u - MID) / (1 - MID));
  }
  return TOE + (HEEL - TOE) * easeInOutCubic((ph - duty) / (1 - duty));
}
